/*!
 * Adapted from:
 * Reconnecting WebSocket
 * by Pedro Ladaria <pedro.ladaria@gmail.com>
 * https://github.com/pladaria/reconnecting-websocket
 * License MIT
 */

export enum WS_ERROR_EVENT_STATUS {
    MAX_RETRIES = 'MAX_RETRIES',
    TIMEOUT = 'TIMEOUT',
}

export class Event {
    public target: any
    public type: string
    constructor(type: string, target: any) {
        this.target = target
        this.type = type
    }
}

export class ErrorEvent extends Event {
    public message: string
    public error: Error
    constructor(error: Error, target: any) {
        super('error', target)
        this.message = error.message
        this.error = error
    }
}

export class CloseEvent extends Event {
    public code: number
    public reason: string
    public wasClean = true
    constructor(code = 1000, reason = '', target: any) {
        super('close', target)
        this.code = code
        this.reason = reason
    }
}

export interface WebSocketEventMap {
    close: CloseEvent
    error: ErrorEvent
    message: MessageEvent
    open: Event
    reconnect: Event
}

export interface WebSocketEventListenerMap {
    close: (event: CloseEvent) => void | { handleEvent: (event: CloseEvent) => void }
    error: (event: ErrorEvent) => void | { handleEvent: (event: ErrorEvent) => void }
    message: (event: MessageEvent) => void | { handleEvent: (event: MessageEvent) => void }
    open: (event: Event) => void | { handleEvent: (event: Event) => void }
    reconnect: (event: Event) => void | { handleEvent: (event: Event) => void }
}

export type Options = {
    maxReconnectionDelay?: number
    minReconnectionDelay?: number
    reconnectionDelayGrowFactor?: number
    minUptime?: number
    connectionTimeout?: number
    maxRetries?: number
    maxEnqueuedMessages?: number
    startClosed?: boolean
    debug?: boolean
    debugName?: string
    keepaliveTimeout?: number
    /**
     * Keepalive delay in milliseconds
     * - undefined: uses default value (4000ms)
     * - number: custom delay
     * - null: disables keepalive (useful for custom health checks like gRPC binary protocol)
     */
    keepaliveDelay?: number | null
    /**
     * Enable jitter to prevent thundering herd (recommended: true)
     * Adds ±20% randomization to reconnection delays
     */
    useJitter?: boolean
}

const DEFAULT = {
    maxReconnectionDelay: 60000,
    minReconnectionDelay: 1000,
    minUptime: 10000,
    reconnectionDelayGrowFactor: 2.0,
    connectionTimeout: 8000,
    maxRetries: 30,
    maxEnqueuedMessages: 100,
    startClosed: false,
    debug: false,
    debugName: 'RWS',
    keepaliveTimeout: 1000,
    keepaliveDelay: 4000,
    useJitter: true,
}

export type UrlProvider = string | (() => string) | (() => Promise<string>)

export type Message = string | ArrayBuffer | Blob | ArrayBufferView

export type ListenersMap = {
    error: Array<WebSocketEventListenerMap['error']>
    message: Array<WebSocketEventListenerMap['message']>
    open: Array<WebSocketEventListenerMap['open']>
    close: Array<WebSocketEventListenerMap['close']>
    reconnect: Array<WebSocketEventListenerMap['reconnect']>
}

export default class ReconnectingWebSocket {
    private _ws?: WebSocket
    private _listeners: ListenersMap = {
        error: [],
        message: [],
        open: [],
        close: [],
        reconnect: [],
    }
    private _retryCount = -1
    private _uptimeTimeout: any
    private _connectTimeout: any
    private _shouldReconnect = true
    private _connectLock = false
    private _exhausted = false
    private _openedAt = 0
    private _binaryType: BinaryType = 'blob'
    private _closeCalled = false
    private _wasOpen = false
    private _messageQueue: Message[] = []
    private _hardDisconnectTimeoutId: NodeJS.Timeout | undefined = undefined
    private _keepaliveSendTimeoutId: NodeJS.Timeout | undefined = undefined
    private _keepaliveTimeoutId: NodeJS.Timeout | undefined = undefined
    private _connectionChangeTimeoutId: NodeJS.Timeout | undefined = undefined
    private _onlineListener?: () => void
    private _offlineListener?: () => void
    private _visibilityChangeListener?: () => void
    private _connectionChangeListener?: () => void

    private readonly _url: UrlProvider
    private readonly _protocols?: string | string[]
    private readonly _options: Options

    constructor(url: UrlProvider, protocols?: string | string[], options: Options = {}) {
        this._url = url
        this._protocols = protocols
        this._options = options
        if (this._options.startClosed) {
            this._shouldReconnect = false
        }
        this._setupNetworkListeners()
        this._connect()
    }

    static get CONNECTING() {
        return 0
    }
    static get OPEN() {
        return 1
    }
    static get CLOSING() {
        return 2
    }
    static get CLOSED() {
        return 3
    }

    get CONNECTING() {
        return ReconnectingWebSocket.CONNECTING
    }
    get OPEN() {
        return ReconnectingWebSocket.OPEN
    }
    get CLOSING() {
        return ReconnectingWebSocket.CLOSING
    }
    get CLOSED() {
        return ReconnectingWebSocket.CLOSED
    }

    get binaryType() {
        return this._ws ? this._ws.binaryType : this._binaryType
    }

    set binaryType(value: BinaryType) {
        this._binaryType = value
        if (this._ws) {
            this._ws.binaryType = value
        }
    }

    /**
     * Returns the number or connection retries
     */
    get retryCount(): number {
        return Math.max(this._retryCount, 0)
    }

    /**
     * The number of bytes of data that have been queued using calls to send() but not yet
     * transmitted to the network. This value resets to zero once all queued data has been sent.
     * This value does not reset to zero when the connection is closed; if you keep calling send(),
     * this will continue to climb. Read only
     */
    get bufferedAmount(): number {
        const bytes = this._messageQueue.reduce((acc, message) => {
            if (typeof message === 'string') {
                acc += message.length // not byte size
            } else if (message instanceof Blob) {
                acc += message.size
            } else {
                acc += message.byteLength
            }
            return acc
        }, 0)
        return bytes + (this._ws ? this._ws.bufferedAmount : 0)
    }

    /**
     * The extensions selected by the server. This is currently only the empty string or a list of
     * extensions as negotiated by the connection
     */
    get extensions(): string {
        return this._ws ? this._ws.extensions : ''
    }

    /**
     * A string indicating the name of the sub-protocol the server selected;
     * this will be one of the strings specified in the protocols parameter when creating the
     * WebSocket object
     */
    get protocol(): string {
        return this._ws ? this._ws.protocol : ''
    }

    /**
     * The current state of the connection; this is one of the Ready state constants
     */
    get readyState(): number {
        if (this._ws) {
            return this._ws.readyState
        }
        return this._options.startClosed ? ReconnectingWebSocket.CLOSED : ReconnectingWebSocket.CONNECTING
    }

    /**
     * The URL as resolved by the constructor
     */
    get url(): string {
        return this._ws ? this._ws.url : ''
    }

    /**
     * Closes the WebSocket connection or connection attempt, if any. If the connection is already
     * CLOSED, this method does nothing
     */
    public close(code = 1000, reason?: string) {
        this._closeCalled = true
        this._shouldReconnect = false
        this._clearTimeouts()
        this._removeNetworkListeners()
        if (!this._ws) {
            this._debug('close enqueued: no ws instance')
            return
        }
        if (this._ws.readyState === this.CLOSED) {
            this._debug('close: already closed')
            return
        }
        this._ws.close(code, reason)
    }

    /**
     * Closes the WebSocket connection or connection attempt and connects again.
     * Resets retry counter;
     */
    public reconnect(code?: number, reason?: string) {
        this._shouldReconnect = true
        this._closeCalled = false
        this._exhausted = false
        this._retryCount = -1
        if (!this._ws || this._ws.readyState === this.CLOSED) {
            this._connect()
        } else {
            this._disconnect(code, reason)
            this._connect()
        }
    }

    /**
     * Keeps the current connection (and its keepalive) but never reconnects once it drops
     */
    public stopReconnecting() {
        this._shouldReconnect = false
    }

    /**
     * Enqueue specified data to be transmitted to the server over the WebSocket connection
     */
    public send(data: Message) {
        if (this._ws && this._ws.readyState === this.OPEN) {
            this._debug('send', data)
            this._ws.send(data)
        } else {
            const { maxEnqueuedMessages = DEFAULT.maxEnqueuedMessages } = this._options
            if (this._messageQueue.length < maxEnqueuedMessages) {
                this._debug('enqueue', data)
                this._messageQueue.push(data)
            }
        }
    }

    /**
     * Register an event handler of a specific event type
     */
    public addEventListener<T extends keyof WebSocketEventListenerMap>(
        type: T,
        listener: WebSocketEventListenerMap[T],
    ): void {
        if (this._listeners[type]) {
            // @ts-ignore
            this._listeners[type].push(listener)
        }
    }

    public dispatchEvent(event: Event) {
        const listeners = this._listeners[event.type as keyof WebSocketEventListenerMap]
        if (listeners) {
            for (const listener of listeners) {
                this._callEventListener(event, listener)
            }
        }
        return true
    }

    /**
     * Removes an event listener
     */
    public removeEventListener<T extends keyof WebSocketEventListenerMap>(
        type: T,
        listener: WebSocketEventListenerMap[T],
    ): void {
        if (this._listeners[type]) {
            // @ts-ignore
            this._listeners[type] = this._listeners[type].filter((l) => l !== listener)
        }
    }

    private _debug(...args: any[]) {
        if (this._options.debug) {
            // not using spread because compiled version uses Symbols
            // tslint:disable-next-line
            console.log.apply(console, [this._options.debugName + '>', ...args])
        }
    }

    private _getNextDelay() {
        const {
            reconnectionDelayGrowFactor = DEFAULT.reconnectionDelayGrowFactor,
            minReconnectionDelay = DEFAULT.minReconnectionDelay,
            maxReconnectionDelay = DEFAULT.maxReconnectionDelay,
            useJitter = DEFAULT.useJitter,
        } = this._options
        let delay = 0
        if (this._retryCount > 0) {
            delay = minReconnectionDelay * Math.pow(reconnectionDelayGrowFactor, this._retryCount - 1)
            if (delay > maxReconnectionDelay) {
                delay = maxReconnectionDelay
            }
        }

        // Add jitter to prevent thundering herd
        if (useJitter && delay > 0) {
            const jitterRange = delay * 0.2 // ±20%
            const jitter = (Math.random() - 0.5) * 2 * jitterRange
            delay = Math.max(0, delay + jitter)
        }

        this._debug('next delay', delay)
        return delay
    }

    private _wait(): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(resolve, this._getNextDelay())
        })
    }

    private _getNextUrl(urlProvider: UrlProvider): Promise<string> {
        if (typeof urlProvider === 'string') {
            return Promise.resolve(urlProvider)
        }
        if (typeof urlProvider === 'function') {
            const url = urlProvider()
            if (typeof url === 'string') {
                return Promise.resolve(url)
            }
            // @ts-ignore redundant check
            if (url.then) {
                return url
            }
        }
        throw Error('Invalid URL')
    }

    private _connect() {
        if (this._connectLock || this._exhausted || !this._shouldReconnect) {
            return
        }

        this._connectLock = true

        const {
            maxRetries = DEFAULT.maxRetries,
            connectionTimeout = DEFAULT.connectionTimeout,
            minUptime = DEFAULT.minUptime,
        } = this._options

        // the minUptime timer doesn't run in throttled background tabs, so also judge the last connection's uptime
        if (this._retryCount > 0 && this._openedAt && Date.now() - this._openedAt >= minUptime) {
            this._retryCount = 0
        }
        this._openedAt = 0

        if (this._retryCount >= maxRetries) {
            this._debug('max retries reached', this._retryCount, '>=', maxRetries)
            this._exhaust()
            return
        }

        this._retryCount++

        this._debug('connect, retry count:', this._retryCount)
        this._removeListeners()
        this._wait()
            .then(() => this._getNextUrl(this._url))
            .then((url) => {
                // close could be called before creating the ws
                if (this._closeCalled) {
                    this._connectLock = false
                    return
                }
                // Every consumer of this class puts an auth token in the query
                // string (?token=...) — strip it before it ever reaches
                // console.log, regardless of which debug:true caller triggered this.
                this._debug('connect', { url: url.split('?')[0], protocols: this._protocols })
                this._ws = this._protocols ? new WebSocket(url, this._protocols) : new WebSocket(url)
                this._ws!.binaryType = this._binaryType
                this._connectLock = false
                this._addListeners()

                this._connectTimeout = setTimeout(this._handleTimeout.bind(this), connectionTimeout)
            })
            .catch((error) => {
                // WebSocket missing (blocked) or the constructor / url provider threw: retrying can't help
                this._debug('connect failed', error)
                this._exhaust()
            })
    }

    // Emits MAX_RETRIES once, after the close/error dispatch in progress so listeners end on the terminal state.
    // Only reconnect(), 'online' or the page becoming visible start a new cycle.
    private _exhaust() {
        this._connectLock = false
        this._exhausted = true
        Promise.resolve().then(() => {
            if (this._exhausted && this._shouldReconnect) {
                this._handleError(new ErrorEvent(Error(WS_ERROR_EVENT_STATUS.MAX_RETRIES), this))
            }
        })
    }

    private _handleTimeout() {
        clearTimeout(this._hardDisconnectTimeoutId)
        this._hardDisconnectTimeoutId = setTimeout(() => {
            this._debug('timeout event')
            const event = new ErrorEvent(Error(WS_ERROR_EVENT_STATUS.TIMEOUT), this)

            this._handleError(event)
        }, 5000)
    }

    private _disconnect(code = 1000, reason?: string) {
        this._clearTimeouts()
        if (!this._ws) {
            return
        }
        this._removeListeners()
        try {
            this._ws.close(code, reason)
            this._handleClose(new CloseEvent(code, reason, this))
        } catch (error) {
            // ignore
        }
    }

    private _keepalive() {
        if (this._ws && this._ws.readyState === this.OPEN) {
            this._debug('sending keepalive')
            // one probe at a time: an extra (visibility / network change) probe replaces the scheduled one
            clearTimeout(this._keepaliveSendTimeoutId)
            clearTimeout(this._keepaliveTimeoutId)
            this._ws.send(
                JSON.stringify({
                    requestType: 'keepAliveRequest',
                    rawData: {},
                }),
            )
            const { keepaliveTimeout = DEFAULT.keepaliveTimeout } = this._options
            this._keepaliveTimeoutId = setTimeout(this._handleTimeout.bind(this), keepaliveTimeout)
        }
    }

    private _acceptOpen() {
        this._debug('accept that opening was successful')
        this._retryCount = 0
    }

    private _callEventListener<T extends keyof WebSocketEventListenerMap>(
        event: WebSocketEventMap[T],
        listener: WebSocketEventListenerMap[T],
    ) {
        if ('handleEvent' in listener) {
            // @ts-ignore
            listener.handleEvent(event)
        } else {
            // @ts-ignore
            listener(event)
        }
    }

    private _handleOpen = (event: Event) => {
        this._debug('open event')
        const { minUptime = DEFAULT.minUptime } = this._options

        const wasOpen = this._wasOpen
        this._wasOpen = true
        this._openedAt = Date.now()

        clearTimeout(this._connectTimeout)
        this._uptimeTimeout = setTimeout(() => this._acceptOpen(), minUptime)

        this._ws!.binaryType = this._binaryType

        // send enqueued messages (messages sent before websocket open event)
        this._messageQueue.forEach((message) => this._ws?.send(message))
        this._messageQueue = []

        // Start keepalive AFTER connection is open
        this._resetKeepaliveSendTimeout()

        // if the connection was previously opened already, then dispatch a reconnect event, otherwise open
        if (!wasOpen) this._listeners.open.forEach((listener) => this._callEventListener(event, listener))
        if (wasOpen) this._listeners.reconnect.forEach((listener) => this._callEventListener(event, listener))
    }

    private _handleMessage = (event: MessageEvent) => {
        this._debug('message event', event.data)

        if (this._isAKeepaliveResponse(event)) {
            this._debug('keepalive response received')
            clearTimeout(this._keepaliveTimeoutId)
            this._resetKeepaliveSendTimeout()
        }

        if (this._hardDisconnectTimeoutId) {
            clearTimeout(this._hardDisconnectTimeoutId)
            this._hardDisconnectTimeoutId = undefined
            // any traffic proves the socket alive, but the unanswered keepalive left the chain unarmed
            this._resetKeepaliveSendTimeout()
        }
        this._listeners.message.forEach((listener) => this._callEventListener(event, listener))
    }

    private _handleError = (event: ErrorEvent) => {
        this._debug('error event', event.message)
        this._disconnect(undefined, event.message === WS_ERROR_EVENT_STATUS.TIMEOUT ? 'timeout' : event.message)

        this._debug('exec error listeners')
        this._listeners.error.forEach((listener) => this._callEventListener(event, listener))

        this._connect()
    }

    private _handleClose = (event: CloseEvent) => {
        this._debug('close event')
        this._clearTimeouts()

        if (this._shouldReconnect) {
            this._connect()
        }
        this._listeners.close.forEach((listener) => this._callEventListener(event, listener))
    }

    private _removeListeners() {
        if (!this._ws) {
            return
        }
        this._debug('removeListeners')
        this._ws.removeEventListener('open', this._handleOpen)
        this._ws.removeEventListener('close', this._handleClose)
        this._ws.removeEventListener('message', this._handleMessage)
        // @ts-ignore
        this._ws.removeEventListener('error', this._handleError)
    }

    private _addListeners() {
        if (!this._ws) {
            return
        }
        this._debug('addListeners')
        this._ws.addEventListener('open', this._handleOpen)
        this._ws.addEventListener('close', this._handleClose)
        this._ws.addEventListener('message', this._handleMessage)
        // @ts-ignore
        this._ws.addEventListener('error', this._handleError)
    }

    private _clearTimeouts() {
        clearTimeout(this._connectTimeout)
        clearTimeout(this._uptimeTimeout)
        clearTimeout(this._keepaliveSendTimeoutId)
        clearTimeout(this._keepaliveTimeoutId)
        clearTimeout(this._hardDisconnectTimeoutId)
        this._hardDisconnectTimeoutId = undefined
        clearTimeout(this._connectionChangeTimeoutId)
    }

    private _resetKeepaliveSendTimeout() {
        clearTimeout(this._keepaliveSendTimeoutId)

        // Check if keepalive is explicitly disabled by checking if it was set to null or false
        // If keepaliveDelay is undefined in options, use the default value
        // If keepaliveDelay is explicitly set to null, keepalive is disabled
        const keepaliveDelay = this._options.keepaliveDelay

        // Use default if not specified, but respect explicit null/false to disable
        if (keepaliveDelay === null) {
            // Explicitly disabled
            return
        }

        const delay = keepaliveDelay !== undefined ? keepaliveDelay : DEFAULT.keepaliveDelay
        this._keepaliveSendTimeoutId = setTimeout(() => this._keepalive(), delay)
    }

    private _isAKeepaliveResponse(event: MessageEvent) {
        try {
            const mData = JSON.parse(event.data)
            return mData.requestType === 'keepAliveRequest'
        } catch {
            return false
        }
    }

    /**
     * Setup network connection listeners to detect when internet comes back online
     * When the device regains internet connection, immediately attempt to reconnect
     * Also includes Page Visibility API for Safari/mobile and Connection API for network changes
     */
    private _setupNetworkListeners() {
        // Only setup in browser environment (not Node.js)
        if (typeof window === 'undefined' || !window.addEventListener) {
            return
        }

        // Standard online/offline events
        this._onlineListener = () => {
            this._debug('network came back online')

            // Only reconnect if we should be connected and currently aren't
            if (this._shouldReconnect && (!this._ws || this._ws.readyState !== this.OPEN)) {
                this._debug('network online: triggering immediate reconnect')
                // Reset retry count for immediate reconnection without delay
                this._retryCount = -1
                this._exhausted = false
                this._connect()
            }
        }

        this._offlineListener = () => {
            this._debug('network went offline')
        }

        window.addEventListener('online', this._onlineListener)
        window.addEventListener('offline', this._offlineListener)

        // Page Visibility API - critical for Safari/mobile browsers
        // Detects when tab/app becomes visible again after being backgrounded
        this._visibilityChangeListener = () => {
            if (typeof document === 'undefined') {
                return
            }

            // When page becomes visible again
            if (!document.hidden) {
                this._debug('page became visible')

                // Check if connection is dead/stale
                if (this._shouldReconnect && (!this._ws || this._ws.readyState !== this.OPEN)) {
                    this._debug('page visible: connection not open, triggering immediate reconnect')
                    // Reset retry count for immediate reconnection
                    this._retryCount = -1
                    this._exhausted = false
                    this._connect()
                } else if (this._ws && this._ws.readyState === this.OPEN) {
                    // Connection appears open, but might be stale (Safari/mobile issue)
                    // Send a keepalive to verify it's actually alive (only if keepalive is enabled)
                    if (this._options.keepaliveDelay !== null) {
                        this._debug('page visible: connection appears open, sending keepalive to verify')
                        this._keepalive()
                    } else {
                        this._debug('page visible: connection appears open, keepalive disabled (custom health check)')
                    }
                }
            } else {
                this._debug('page became hidden')
            }
        }

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._visibilityChangeListener)
            this._debug('page visibility listener setup')
        }

        // Network Information API - detects network type changes (WiFi <-> Cellular)
        // Supported in Chrome mobile, some other mobile browsers
        if ('connection' in navigator) {
            this._connectionChangeListener = () => {
                // @ts-ignore - NetworkInformation API
                const connection = navigator.connection
                this._debug('network type changed', {
                    effectiveType: connection?.effectiveType,
                    downlink: connection?.downlink,
                    rtt: connection?.rtt,
                })

                // Network type changed (e.g., WiFi -> 4G), test the existing connection first
                if (this._shouldReconnect && this._ws && this._ws.readyState === this.OPEN) {
                    // Only test with keepalive if keepalive is enabled
                    // If keepalive is disabled (null), custom health checks will detect issues
                    if (this._options.keepaliveDelay !== null) {
                        this._debug('network type changed: testing existing connection with keepalive')
                        // Give it a moment for the network to stabilize, then test with keepalive
                        // If the connection is dead, the keepalive timeout will trigger reconnection
                        // 'change' fires in bursts: keep a single pending probe
                        clearTimeout(this._connectionChangeTimeoutId)
                        this._connectionChangeTimeoutId = setTimeout(() => {
                            this._keepalive()
                        }, 1000)
                    } else {
                        this._debug('network type changed: keepalive disabled, relying on custom health checks')
                    }
                }
            }

            // @ts-ignore - NetworkInformation API
            navigator.connection.addEventListener('change', this._connectionChangeListener)
            this._debug('connection change listener setup')
        }

        this._debug('network listeners setup complete')
    }

    /**
     * Remove network connection listeners
     */
    private _removeNetworkListeners() {
        if (typeof window === 'undefined' || !window.removeEventListener) {
            return
        }

        if (this._onlineListener) {
            window.removeEventListener('online', this._onlineListener)
            this._onlineListener = undefined
        }

        if (this._offlineListener) {
            window.removeEventListener('offline', this._offlineListener)
            this._offlineListener = undefined
        }

        if (this._visibilityChangeListener && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._visibilityChangeListener)
            this._visibilityChangeListener = undefined
        }

        if (this._connectionChangeListener && 'connection' in navigator) {
            // @ts-ignore - NetworkInformation API
            navigator.connection.removeEventListener('change', this._connectionChangeListener)
            this._connectionChangeListener = undefined
        }

        this._debug('network listeners removed')
    }
}
